import { useMainStore } from '@/composables/store'
import {
  captureRedirectTrackingParamsFromUrl,
  getRedirectAwareHost,
  getRedirectTrackingQuery,
} from '@/utils/eventTracking'
import { getCurrentServer, getToken } from '@/utils/globalData'
import {
  getOnlineVisitorId,
  resolveOnlineVisitShopId,
} from '@/utils/onlineVisit'

const CLIENT_EVENT_LOG_ENDPOINT = '/api/mall/frontserver/clientEventLog/record'
const ATTRIBUTION_STORAGE_KEY = 'mall_web_attribution_context_v1'
const QUEUE_STORAGE_KEY = 'mall_client_event_log_queue_v1'
const ACK_STORAGE_KEY = 'mall_client_event_log_ack_v1'
const SESSION_STORAGE_KEY = 'mall_client_event_log_session_id_v1'
const ATTRIBUTION_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000
const QUEUE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
const MAX_QUEUE_SIZE = 200
const MAX_ACK_SIZE = 200
const REQUEST_TIMEOUT_MS = 8000
const RETRY_BASE_MS = 1500
const RETRY_MAX_MS = 5 * 60 * 1000

const AD_QUERY_KEYS = Object.freeze([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_id',
  'utm_content',
  'utm_term',
  'fbclid',
  'fbcid',
  'ttclid',
  'campaign',
  'campaign_id',
  'campaignid',
  'fb_campaign_id',
  'tt_campaign_id',
  'adset',
  'adset_id',
  'adsetid',
  'adset_name',
  'adgroup_id',
  'adgroupid',
  'adgroup_name',
  'tt_adgroup_id',
  'ad',
  'ad_id',
  'adid',
  'ad_name',
  'tt_ad_id',
  'placement',
  'source',
])

let initialized = false
let queueCache = null
let ackCache = null
let flushPromise = null
let flushTimer = null

const isClientRuntime = () => typeof window !== 'undefined' && typeof document !== 'undefined'
const normalizeText = (value, maxLength = 255) => String(value ?? '').trim().slice(0, maxLength)

function firstNonEmpty(...values) {
  for (const value of values) {
    const normalizedValue = normalizeText(value, 1000)
    if (normalizedValue)
      return normalizedValue
  }
  return ''
}

function runSafely(callback, fallback = null) {
  try {
    return callback()
  }
  catch {
    return fallback
  }
}

function safeJsonParse(value, fallback) {
  try {
    return JSON.parse(value)
  }
  catch {
    return fallback
  }
}

function safeStorageGet(storage, key) {
  if (!isClientRuntime())
    return ''
  try {
    return storage?.getItem(key) || ''
  }
  catch {
    return ''
  }
}

function safeStorageSet(storage, key, value) {
  if (!isClientRuntime())
    return false
  try {
    storage?.setItem(key, value)
    return true
  }
  catch {
    return false
  }
}

function randomId(prefix = 'web') {
  if (isClientRuntime() && window.crypto?.randomUUID) {
    return `${prefix}_${window.crypto.randomUUID()}`
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
}

function getSessionId() {
  const storedSessionId = normalizeText(
    safeStorageGet(window.sessionStorage, SESSION_STORAGE_KEY),
    100,
  )
  if (storedSessionId)
    return storedSessionId

  const sessionId = randomId('web_session').slice(0, 100)
  safeStorageSet(window.sessionStorage, SESSION_STORAGE_KEY, sessionId)
  return sessionId
}

function getMainStoreSafely() {
  try {
    return useMainStore()
  }
  catch {
    return null
  }
}

function getSafeReferrer() {
  if (!isClientRuntime() || !document.referrer)
    return ''
  try {
    const url = new URL(document.referrer)
    return `${url.origin}${url.pathname}`.slice(0, 1000)
  }
  catch {
    return ''
  }
}

function getReferrerHost() {
  const referrer = getSafeReferrer()
  if (!referrer)
    return ''
  try {
    return normalizeText(new URL(referrer).hostname, 255).toLowerCase()
  }
  catch {
    return ''
  }
}

function filterAdQuery(query = {}) {
  return AD_QUERY_KEYS.reduce((result, key) => {
    const value = normalizeText(query[key], 500)
    if (value)
      result[key] = value
    return result
  }, {})
}

function collectAdQuery(inputUrl) {
  if (!isClientRuntime())
    return {}

  try {
    const currentUrl = inputUrl instanceof URL
      ? inputUrl
      : new URL(inputUrl || window.location.href, window.location.origin)
    captureRedirectTrackingParamsFromUrl(currentUrl)
    const storedQuery = getRedirectTrackingQuery()
    const currentQuery = {}
    currentUrl.searchParams.forEach((value, key) => {
      currentQuery[key] = value
    })
    return filterAdQuery({
      ...storedQuery,
      ...currentQuery,
    })
  }
  catch {
    return {}
  }
}

function inferAttributionSource(query = {}, referrerHost = '') {
  const sourceText = firstNonEmpty(query.utm_source, query.source).toLowerCase()
  const referrer = normalizeText(referrerHost, 255).toLowerCase()

  if (
    query.ttclid
    || query.tt_campaign_id
    || query.tt_adgroup_id
    || query.tt_ad_id
    || /(?:^|[^a-z])(?:tiktok|tik_tok|tt)(?:[^a-z]|$)/i.test(sourceText)
    || /(?:^|\.)tiktok\.com$/i.test(referrer)
  ) {
    return 'tiktok'
  }

  if (
    query.fbclid
    || query.fbcid
    || query.fb_campaign_id
    || /(?:^|[^a-z])(?:facebook|meta|instagram|fb|ig)(?:[^a-z]|$)/i.test(sourceText)
    || /(?:^|\.)(?:facebook|instagram)\.com$/i.test(referrer)
  ) {
    return 'meta'
  }

  if (query.utm_source || query.utm_campaign || query.utm_id)
    return 'utm'
  if (referrer)
    return 'referral'
  return 'direct'
}

function buildSafeLandingUrl(query = {}) {
  if (!isClientRuntime())
    return ''
  try {
    const host = getRedirectAwareHost() || window.location.host
    const url = new URL(window.location.href)
    if (host)
      url.host = host
    url.search = ''
    Object.entries(filterAdQuery(query)).forEach(([key, value]) => {
      url.searchParams.set(key, value)
    })
    url.hash = ''
    return url.toString().slice(0, 3000)
  }
  catch {
    return ''
  }
}

function buildAttributionTouch(inputUrl) {
  const query = collectAdQuery(inputUrl)
  const referrerHost = getReferrerHost()
  const source = inferAttributionSource(query, referrerHost)
  const campaignId = firstNonEmpty(
    query.campaign_id,
    query.campaignid,
    query.fb_campaign_id,
    query.tt_campaign_id,
    query.utm_id,
  )
  const adSetId = firstNonEmpty(
    query.adset_id,
    query.adsetid,
    query.adgroup_id,
    query.adgroupid,
    query.tt_adgroup_id,
    query.utm_term,
  )
  const adId = firstNonEmpty(
    query.ad_id,
    query.adid,
    query.tt_ad_id,
    query.utm_content,
  )

  return {
    source,
    isAttributed: source !== 'direct' && source !== 'referral',
    mediaSource: firstNonEmpty(query.utm_source, query.source, source),
    medium: normalizeText(query.utm_medium, 255),
    campaign: firstNonEmpty(query.utm_campaign, query.campaign, campaignId),
    campaignId,
    adSetId,
    adSetName: firstNonEmpty(query.adset_name, query.adgroup_name, query.adset),
    adId,
    adName: firstNonEmpty(query.ad_name, query.ad),
    placement: normalizeText(query.placement, 255),
    fbclid: firstNonEmpty(query.fbclid, query.fbcid),
    ttclid: normalizeText(query.ttclid, 500),
    referrerHost,
    landingUrl: buildSafeLandingUrl(query),
    query,
    capturedAt: new Date().toISOString(),
  }
}

function isStoredAttributionFresh(context) {
  return context
    && Number(context.updatedAt) > 0
    && Date.now() - Number(context.updatedAt) <= ATTRIBUTION_MAX_AGE_MS
}

function readStoredAttribution() {
  const parsed = safeJsonParse(
    safeStorageGet(window.localStorage, ATTRIBUTION_STORAGE_KEY),
    null,
  )
  return isStoredAttributionFresh(parsed) ? parsed : null
}

export function captureWebAttribution(inputUrl) {
  if (!isClientRuntime())
    return null

  const currentTouch = buildAttributionTouch(inputUrl)
  const storedContext = readStoredAttribution()
  const hasNewAttributionSignal = currentTouch.isAttributed
    || currentTouch.source === 'referral'
    || Boolean(currentTouch.fbclid || currentTouch.ttclid)
  const firstTouch = storedContext?.firstTouch || currentTouch
  const lastTouch = hasNewAttributionSignal
    ? currentTouch
    : (storedContext?.lastTouch || currentTouch)
  const context = {
    firstTouch,
    lastTouch,
    currentTouch,
    updatedAt: Date.now(),
  }

  safeStorageSet(window.localStorage, ATTRIBUTION_STORAGE_KEY, JSON.stringify(context))
  return context
}

export function getWebAttributionContext() {
  if (!isClientRuntime())
    return null
  return captureWebAttribution() || readStoredAttribution()
}

function getResolvedAttribution() {
  const context = getWebAttributionContext()
  const currentTouch = context?.currentTouch || {}
  const lastTouch = context?.lastTouch || {}
  const resolvedTouch = currentTouch.isAttributed ? currentTouch : lastTouch

  return {
    context,
    touch: resolvedTouch || {},
    isAttributed: Boolean(resolvedTouch?.isAttributed),
  }
}

function getPlatformType() {
  if (!isClientRuntime())
    return 'pc-web'
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent || '')
    ? 'h5'
    : 'pc-web'
}

function getDeviceType() {
  if (!isClientRuntime())
    return ''
  const ua = navigator.userAgent || ''
  if (/ipad|tablet/i.test(ua))
    return 'tablet'
  if (/android|iphone|ipod|mobile/i.test(ua))
    return 'mobile'
  return 'desktop'
}

function getUserContext() {
  const store = getMainStoreSafely()
  const user = store?.mallUserInfo || store?.userInfo || {}
  const userId = firstNonEmpty(user.id, user.userId, user.customerInfoId)
  const memberCode = firstNonEmpty(user.memberCode, user.code, userId)

  return {
    userId,
    memberCode,
    userName: firstNonEmpty(user.userName, user.username, user.account),
    nickname: firstNonEmpty(user.nickname, user.nickName),
    memberName: firstNonEmpty(user.memberName, user.name),
    displayName: firstNonEmpty(user.displayName, user.nickname, user.name),
    isLogin: Boolean(userId || getToken()),
  }
}

function getSiteContext() {
  const store = getMainStoreSafely()
  const siteInfo = typeof store?.getSiteInfo === 'function'
    ? (store.getSiteInfo() || {})
    : (store?.siteInfo || {})
  const domain = getRedirectAwareHost() || window.location.host
  const shopId = resolveOnlineVisitShopId()

  return {
    domain: normalizeText(domain, 255),
    shopId: normalizeText(shopId, 100),
    siteName: firstNonEmpty(
      siteInfo.pc_SITE_NAME,
      siteInfo.mobile_SITE_NAME,
      siteInfo.title,
    ),
    appType: firstNonEmpty(siteInfo.siteCode, domain, 'mall-web').slice(0, 100),
  }
}

function buildBasePayload({ eventName, eventModule, eventSource, recordKey }) {
  const user = getUserContext()
  const site = getSiteContext()
  const visitorId = getOnlineVisitorId()
  const sessionId = getSessionId()
  const attribution = getResolvedAttribution()
  const pageRoute = `${window.location.pathname || '/'}${window.location.hash || ''}`

  return {
    recordKey,
    clientType: 'pc',
    appType: site.appType,
    platformType: getPlatformType(),
    channel: firstNonEmpty(attribution.touch.source, 'direct'),
    memberCode: user.memberCode || `guest:${visitorId}`,
    userId: user.userId || undefined,
    userName: user.userName || undefined,
    nickname: user.nickname || undefined,
    memberName: user.memberName || undefined,
    displayName: user.displayName || undefined,
    isLogin: user.isLogin,
    sessionId,
    deviceNo: visitorId,
    deviceType: getDeviceType(),
    userAgent: navigator.userAgent || '',
    currentServer: firstNonEmpty(getCurrentServer(), window.location.origin),
    eventName,
    eventModule,
    eventSource,
    eventStatus: 'success',
    pageRoute: normalizeText(pageRoute, 255),
    occurredAt: new Date().toISOString(),
    itemId: recordKey,
    isAttributed: attribution.isAttributed,
    attributionSource: attribution.touch.source || 'direct',
    campaign: firstNonEmpty(attribution.touch.campaign, attribution.touch.campaignId),
    mediaSource: firstNonEmpty(attribution.touch.mediaSource, attribution.touch.source),
    deepLinkValue: normalizeText(attribution.touch.landingUrl, 255),
    oneLinkUrl: normalizeText(attribution.touch.landingUrl, 3000),
    payload: {
      clientEventId: recordKey,
      domain: site.domain,
      shopId: site.shopId,
      siteName: site.siteName,
      referrer: getSafeReferrer(),
      attribution: attribution.context,
    },
    tags: {
      clientEventId: recordKey,
      attributionSource: attribution.touch.source || 'direct',
      campaignId: attribution.touch.campaignId || '',
      adSetId: attribution.touch.adSetId || '',
      adSetName: attribution.touch.adSetName || '',
      adId: attribution.touch.adId || '',
      adName: attribution.touch.adName || '',
      placement: attribution.touch.placement || '',
      fbclid: attribution.touch.fbclid || '',
      ttclid: attribution.touch.ttclid || '',
    },
  }
}

function pruneQueue(queue) {
  return (Array.isArray(queue) ? queue : [])
    .filter(item => item?.recordKey && item?.payload && Date.now() - Number(item.createdAt) <= QUEUE_MAX_AGE_MS)
    .slice(-MAX_QUEUE_SIZE)
}

function readQueue() {
  if (queueCache)
    return queueCache
  queueCache = pruneQueue(safeJsonParse(
    safeStorageGet(window.localStorage, QUEUE_STORAGE_KEY),
    [],
  ))
  return queueCache
}

function writeQueue(queue) {
  queueCache = pruneQueue(queue)
  safeStorageSet(window.localStorage, QUEUE_STORAGE_KEY, JSON.stringify(queueCache))
}

function readAckKeys() {
  if (ackCache)
    return ackCache
  const parsed = safeJsonParse(safeStorageGet(window.localStorage, ACK_STORAGE_KEY), [])
  ackCache = Array.isArray(parsed) ? parsed.slice(-MAX_ACK_SIZE) : []
  return ackCache
}

function markAcknowledged(recordKey) {
  ackCache = [
    ...readAckKeys().filter(item => item !== recordKey),
    recordKey,
  ].slice(-MAX_ACK_SIZE)
  safeStorageSet(window.localStorage, ACK_STORAGE_KEY, JSON.stringify(ackCache))
}

function scheduleFlush(delay = 0) {
  if (!isClientRuntime())
    return
  if (flushTimer)
    window.clearTimeout(flushTimer)
  flushTimer = window.setTimeout(() => {
    flushTimer = null
    void flushClientEventLogQueue()
  }, Math.max(0, delay))
}

function enqueuePayload(payload) {
  if (!isClientRuntime() || !payload?.recordKey)
    return false
  if (readAckKeys().includes(payload.recordKey))
    return false

  const queue = readQueue()
  if (queue.some(item => item.recordKey === payload.recordKey)) {
    scheduleFlush(100)
    return false
  }

  queue.push({
    recordKey: payload.recordKey,
    payload,
    createdAt: Date.now(),
    retryCount: 0,
    nextRetryAt: Date.now(),
  })
  writeQueue(queue)
  scheduleFlush(0)
  return true
}

function bumpRetry(recordKey) {
  const queue = readQueue()
  let nextRetryAt = Date.now() + RETRY_MAX_MS
  const nextQueue = queue.map((item) => {
    if (item.recordKey !== recordKey)
      return item
    const retryCount = Number(item.retryCount || 0) + 1
    const delay = Math.min(RETRY_BASE_MS * (2 ** Math.min(retryCount - 1, 8)), RETRY_MAX_MS)
    nextRetryAt = Date.now() + delay
    return {
      ...item,
      retryCount,
      nextRetryAt,
    }
  })
  writeQueue(nextQueue)
  scheduleFlush(Math.max(RETRY_BASE_MS, nextRetryAt - Date.now()))
}

async function sendQueueItem(item, options = {}) {
  const controller = typeof AbortController !== 'undefined' && !options.keepalive
    ? new AbortController()
    : null
  const timer = controller
    ? window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
    : null

  try {
    const response = await fetch(CLIENT_EVENT_LOG_ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(item.payload),
      keepalive: Boolean(options.keepalive),
      signal: controller?.signal,
    })
    if (!response.ok)
      throw new Error(`client event log request failed: ${response.status}`)
    const result = await response.json().catch(() => ({ success: true }))
    if (result?.success === false)
      throw new Error(result.message || 'client event log request failed')
    return true
  }
  finally {
    if (timer)
      window.clearTimeout(timer)
  }
}

export function flushClientEventLogQueue(options = {}) {
  if (!isClientRuntime() || navigator.onLine === false)
    return Promise.resolve(false)
  if (flushPromise)
    return flushPromise

  flushPromise = (async () => {
    const readyItems = readQueue()
      .filter(item => Number(item.nextRetryAt || 0) <= Date.now())
      .slice(0, 10)

    for (const item of readyItems) {
      try {
        await sendQueueItem(item, options)
        writeQueue(readQueue().filter(entry => entry.recordKey !== item.recordKey))
        markAcknowledged(item.recordKey)
      }
      catch {
        bumpRetry(item.recordKey)
        break
      }
    }

    if (readQueue().some(item => Number(item.nextRetryAt || 0) <= Date.now())) {
      scheduleFlush(250)
    }
    return true
  })().finally(() => {
    flushPromise = null
  })

  return flushPromise
}

function buildRecordKey(parts = []) {
  return parts
    .map(value => normalizeText(value, 255).toLowerCase().replace(/[^a-z0-9:_-]+/g, '_'))
    .filter(Boolean)
    .join(':')
    .slice(0, 100)
}

function reportWebOpenInternal() {
  if (!isClientRuntime())
    return false
  const site = getSiteContext()
  const recordKey = buildRecordKey([
    'web',
    'open',
    site.domain,
    getSessionId(),
    Date.now(),
    Math.random().toString(36).slice(2, 8),
  ])
  const payload = buildBasePayload({
    eventName: 'web_open',
    eventModule: 'lifecycle',
    eventSource: 'web_boot',
    recordKey,
  })
  payload.itemType = 'web_lifecycle'
  payload.itemName = 'Web open'
  payload.targetRoute = payload.pageRoute
  payload.payload = {
    ...payload.payload,
    trigger: 'web_boot',
  }
  return enqueuePayload(payload)
}

export function reportWebOpen() {
  return runSafely(reportWebOpenInternal, false)
}

function reportWebOrderSuccessInternal(order = {}) {
  if (!isClientRuntime())
    return false

  const site = getSiteContext()
  const orderId = firstNonEmpty(order.orderId, order.dealerOrderPrimaryId)
  const orderCode = firstNonEmpty(order.orderCode, order.orderNumber)
  const payOrderCode = firstNonEmpty(order.payOrderCode, order.payOrderNo)
  const primaryOrderKey = firstNonEmpty(payOrderCode, orderCode, orderId)
  if (!primaryOrderKey)
    return false

  const items = (Array.isArray(order.items) ? order.items : [])
    .slice(0, 100)
    .map(item => ({
      skuId: firstNonEmpty(item.skuId, item.offerId, item.id),
      spuId: firstNonEmpty(item.spuId, item.spItemSpuId),
      title: firstNonEmpty(item.title, item.subject, item.spuName, item.dealerItemSpuTitle),
      quantity: Number(item.buyAmount || item.quantity || 1),
      price: Number(item.price || item.salePrice || item.unitPrice || 0),
    }))
  const recordKey = buildRecordKey(['web', 'order_success', site.domain, primaryOrderKey])
  const totalQuantity = items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)
  const amount = Number(order.amount ?? order.totalAmount ?? 0)
  const payload = buildBasePayload({
    eventName: 'order_success',
    eventModule: 'order',
    eventSource: 'payment_result',
    recordKey,
  })

  payload.itemType = 'order'
  payload.itemName = `Web order success ${primaryOrderKey}`.slice(0, 255)
  payload.itemPrice = Number.isFinite(amount) ? amount : undefined
  payload.orderId = orderId || undefined
  payload.orderCode = orderCode || undefined
  payload.payOrderCode = payOrderCode || undefined
  payload.targetRoute = '/payment/successful'
  payload.payload = {
    ...payload.payload,
    orderId,
    orderCode,
    payOrderCode,
    amount: Number.isFinite(amount) ? amount : 0,
    currency: firstNonEmpty(order.currency, 'TWD'),
    totalQuantity,
    payType: firstNonEmpty(order.payType, order.paymentType),
    status: firstNonEmpty(order.status, 'success'),
    source: firstNonEmpty(order.source, 'payment_successful_page'),
    items,
  }
  return enqueuePayload(payload)
}

export function reportWebOrderSuccess(order = {}) {
  return runSafely(() => reportWebOrderSuccessInternal(order), false)
}

function setupRuntimeListeners() {
  window.addEventListener('online', () => scheduleFlush(100))
  window.addEventListener('pagehide', () => {
    void flushClientEventLogQueue({ keepalive: true })
  })
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      void flushClientEventLogQueue({ keepalive: true })
    }
  })
}

export function initializeClientEventLog(nuxtApp) {
  if (initialized || !isClientRuntime())
    return
  initialized = true
  runSafely(() => captureWebAttribution())
  runSafely(() => setupRuntimeListeners())
  runSafely(() => nuxtApp?.$router?.afterEach?.(() => {
    runSafely(() => captureWebAttribution())
  }))
  window.setTimeout(() => {
    runSafely(() => reportWebOpen())
    runSafely(() => void flushClientEventLogQueue())
  })
}
